import { cn } from '@/lib/cn';
import { DashboardTooltip } from 'components/tooltip/commonTooltips/DashboardTooltip';
import { usePluginDashboardList } from 'queries/hooks/usePluginDashboardList';
import React from 'react';
import { DashboardFolder, DashboardItem } from 'services/PluginService';
import { NavLink } from 'ui/nav/components/NavLink';
import { DetailHeader } from './DetailHeader';

interface OverviewDashboardsProps {
    pluginId: string;
    configId: string;
}

export const OverviewDashboards: React.FC<OverviewDashboardsProps> = ({ pluginId, configId }) => {
    const { data: dashboards = [] } = usePluginDashboardList(pluginId, configId);

    if (dashboards.length === 0) {
        return null;
    }

    return (
        <div className='flex-1 p-4 overflow-hidden rounded-lg bg-tileBackground'>
            <div className='flex flex-col h-full' aria-label='overviewDataStreamDetails'>
                <DetailHeader name='Dashboards' tooltip={<DashboardTooltip placement='top' />} />
                <hr className='h-px mx-4 my-4 border-0 bg-dividerPrimary shrink-0' />
                <div className='flex flex-col min-h-0 px-4 overflow-auto table-scroll-overflow'>
                    <DashboardFolderGroup dashboards={dashboards} configId={configId} />
                </div>
            </div>
        </div>
    );
};

interface DashboardLinkProps {
    name: string;
    path: string;
    configId: string;
}

const DashboardLink: React.FC<DashboardLinkProps> = ({ name, path, configId }) => {
    return (
        <NavLink
            className='text-textLink hover:underline hover:text-textLink'
            to={`/datasource/${configId}/dashboard/${path}`}
        >
            {name}
        </NavLink>
    );
};

interface DashboardFolderGroupProps {
    dashboards: (DashboardItem | DashboardFolder)[];
    configId: string;
    level?: number;
}

const DashboardFolderGroup: React.FC<DashboardFolderGroupProps> = ({ dashboards, configId, level = 0 }) => {
    return (
        <div className={cn('flex flex-col', level > 0 && 'ml-6')}>
            {dashboards.map((dashboard) =>
                dashboard.isFolder ? (
                    <div className='relative'>
                        <div
                            data-type='border'
                            className='absolute bottom-0 w-px border-l border-l-dividerTertiary'
                            style={{
                                height: 'calc(100% - 30px)',
                                top: 24,
                                left: 5
                            }}
                        />
                        <div>
                            <div className='flex mb-1'>
                                <p className='text-textSecondary'>{dashboard.name}</p>
                            </div>
                            <DashboardFolderGroup
                                dashboards={dashboard.dashboards}
                                configId={configId}
                                key={dashboard.path}
                                level={level + 1}
                            />
                        </div>
                    </div>
                ) : (
                    <div className={cn(level > 0 && 'ml-1')}>
                        <DashboardLink
                            key={dashboard.path}
                            name={dashboard.name}
                            path={dashboard.path}
                            configId={configId}
                        />
                    </div>
                )
            )}
        </div>
    );
};
